import React, { useMemo } from "react";
import { useStockToFlowChart } from "@/hooks/coinglass/useStockToFlowChart";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import type { EChartsOption } from "echarts";

interface StockToFlowChartProps {
  startDate?: number;
  endDate?: number;
  limit?: number;
  height?: string | number;
}

export const StockToFlowChart: React.FC<StockToFlowChartProps> = ({
  startDate,
  endDate,
  limit,
  height = 500,
}) => {
  const { data, isLoading, isError, error } = useStockToFlowChart({
    start_timestamp: startDate,
    end_timestamp: endDate,
    limit,
  });

  const option = useMemo<EChartsOption | null>(() => {
    if (!data?.data) return null;

    const { xAxis, series } = data.data;
    const halvingDates = ['2012-11-28', '2016-07-09', '2020-05-11', '2024-04-20'];

    return {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        backgroundColor: "rgba(20, 20, 30, 0.9)",
        borderColor: "#333",
        textStyle: { color: "#fff" },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: (params: any) => {
          if (!Array.isArray(params) || params.length === 0) return "";
          
          const p = params;
          const date = p[0].axisValue;
          const price = p[0]?.data;
          const daysToHalving = p[1]?.data;

          return `
            <div style="padding: 8px; min-width: 200px;">
              <div style="font-weight: bold; margin-bottom: 8px; color: #9ca3af;">${date}</div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: #d1d5db;">BTC Price:</span>
                <span style="font-weight: bold; color: #fbbf24;">$${typeof price === 'number' ? price.toLocaleString() : price}</span>
              </div>
              ${daysToHalving !== undefined ? `
              <div style="display: flex; justify-content: space-between;">
                <span style="color: #d1d5db;">Days to Halving:</span>
                <span style="font-weight: bold; color: #3b82f6;">${daysToHalving}</span>
              </div>
              ` : ''}
            </div>
          `;
        }
      },
      legend: {
        data: ["BTC Price", "Days to Next Halving"],
        textStyle: { color: "#9ca3af" },
        top: 0,
        left: "center",
        icon: "circle",
      },
      grid: {
        left: "3%",
        right: "3%",
        bottom: "15%",
        top: "12%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: xAxis,
        axisLabel: {
          color: "#9ca3af",
          hideOverlap: true,
          formatter: (value: string) => {
            const date = new Date(value);
            return date.toLocaleDateString("en-US", {
              month: "short",
              year: "numeric",
            });
          },
        },
        axisLine: { lineStyle: { color: "#374151" } },
      },
      yAxis: [
        {
          type: "value",
          name: "Days to Halving",
          position: "left",
          splitLine: { lineStyle: { color: "#1f2937" } },
          axisLabel: { color: "#3b82f6" },
          axisLine: { lineStyle: { color: "#3b82f6" } },
        },
        {
          type: "value",
          name: "BTC Price (USD)",
          position: "right",
          splitLine: { show: false },
          axisLabel: {
            color: "#9ca3af",
            fontWeight: "normal",
            formatter: (value: number) => {
              if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
              return `$${value}`;
            },
          },
          axisLine: { lineStyle: { color: "#374151" } },
        },
      ],
      series: series.map((s, index) => ({
        ...s,
        yAxisIndex: index,
        itemStyle: { color: index === 0 ? "#fbbf24" : "#3b82f6" },
        lineStyle: { 
          width: 2,
          type: index === 1 ? "dashed" : "solid"
        },
        symbol: "none",
        markLine: index === 0 ? {
          silent: true,
          symbol: "none",
          data: halvingDates.map(date => ({
            xAxis: date,
            label: { 
              formatter: "Halving",
              position: "end",
              color: "#ef4444"
            },
            lineStyle: { color: "#ef4444", type: "solid", width: 1 }
          }))
        } : undefined
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      })) as any,
      dataZoom: [
        { type: "inside", start: 70, end: 100 },
        {
          type: "slider",
          show: true,
          bottom: 30,
          height: 20,
          borderColor: "#374151",
          textStyle: { color: "#9ca3af" },
          brushSelect: false,
        },
      ],
    };
  }, [data]);

  return (
    <EChartsRenderer
      chartOption={option || {}}
      height={height}
      isLoading={isLoading}
      error={isError ? (error as Error).message : null}
      hasData={!!data?.data}
      containerClassName="w-full h-full"
    />
  );
};
